<template>
  <div class="admin-dashboard">
    <!-- Welcome -->
    <div class="welcome-section"
      style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;">
      <div>
        <h2 class="welcome-title">Tableau de bord administrateur</h2>
        <p class="welcome-subtitle">Sélectionnez un employé pour consulter ses informations</p>
      </div>
      <v-btn color="#43a047" variant="flat" style="color:white;" rounded="lg" size="small"
        @click="showGlobalExportDialog = true">
        <v-icon start size="16">mdi-microsoft-excel</v-icon>
        Exporter tous les congés validés
      </v-btn>
    </div>

    <!-- Employees section -->
    <div class="employees-section">
      <div class="section-header">
        <h3 class="section-title">Mes employés</h3>
        <v-chip color="#3f51b5" variant="flat" size="small">
          {{ allUsers.length }}
        </v-chip>
      </div>

      <v-text-field v-model="employeeSearch" placeholder="Rechercher un employé..." prepend-inner-icon="mdi-magnify"
        variant="outlined" density="compact" rounded="lg" clearable hide-details class="emp-search" />

      <div v-if="filteredEmployees.length === 0" class="empty-state">
        <v-icon color="#bdbdbd" size="52">mdi-account-group-outline</v-icon>
        <p>{{ allUsers.length === 0 ? "Chargement des employés..." : "Aucun employé trouvé" }}</p>
      </div>

      <v-row v-else class="emp-grid">
        <v-col v-for="emp in filteredEmployees" :key="emp.user_email || emp.user_name" cols="12" sm="6" md="4" lg="3">
          <div class="emp-card" :style="{ borderLeft: `4px solid ${employeeColor(emp)}` }" @click="openEmployee(emp)">
            <div v-if="employeePendingCount(emp) > 0" class="emp-badge-dot">{{ employeePendingCount(emp) }}</div>
            <div class="emp-names">
              <span class="emp-firstname">{{ employeeFirstName(emp) }}</span>
              <span class="emp-lastname">{{ employeeLastName(emp) }}</span>
            </div>
            <div v-if="emp.service" class="emp-service">
              <v-icon size="11" color="#9e9e9e">mdi-briefcase-outline</v-icon>
              {{ emp.service }}
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Employee detail dialog -->
    <v-dialog v-model="showEmployeeDialog" max-width="1260" scrollable>
      <v-card v-if="selectedEmployee" rounded="xl">
        <!-- Header -->
        <div class="dialog-header" :style="{ borderBottom: `3px solid ${employeeColor(selectedEmployee)}` }">
          <div class="dialog-avatar"
            :style="{ background: `linear-gradient(135deg, ${employeeColor(selectedEmployee)}, ${employeeColorDark(selectedEmployee)})` }">
            {{ employeeInitials(selectedEmployee) }}
          </div>
          <div class="dialog-info">
            <div class="dialog-name">{{ selectedEmployee.user_name || "—" }}</div>
            <div class="dialog-email">
              <v-icon size="13" color="#9e9e9e">mdi-email-outline</v-icon>
              {{ selectedEmployee.user_email || "—" }}
            </div>
            <div v-if="formatCommentaire(selectedEmployee.commentaire)" class="dialog-comment">
              {{ formatCommentaire(selectedEmployee.commentaire) }}
            </div>
          </div>
          <div class="ml-auto" style="display:flex;align-items:center;gap:8px;">
            <v-btn color="#3f51b5" variant="flat" style="color:white;" rounded="lg" size="small"
              @click="showNewRequestDialog = true">
              <v-icon start size="16">mdi-plus</v-icon>
              Nouvelle demande
            </v-btn>
            <v-btn icon variant="text" color="#757575" size="small" @click="showEmployeeDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-card-text class="dialog-body">
          <!-- Contract info -->
          <v-card variant="flat">
            <v-expansion-panels class="" variant="accordion">
              <v-expansion-panel>
                <v-expansion-panel-title>
                  <div style="display:flex; align-items:center; gap:8px;">
                    <v-icon size="16" :color="employeeColor(selectedEmployee)">mdi-file-document-outline</v-icon>
                    <span
                      style="font-size:13px; font-weight:700; color:#424242; text-transform:uppercase; letter-spacing:0.5px;">
                      Informations contractuelles
                    </span>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-row class="mt-1">
                    <v-col cols="6" sm="4">
                      <div class="dialog-tile">
                        <v-icon size="18" :color="employeeColor(selectedEmployee)">mdi-briefcase-outline</v-icon>
                        <span class="dialog-tile-label">Service</span>
                        <span class="dialog-tile-value">{{ selectedEmployee.service || "—" }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <div class="dialog-tile">
                        <v-icon size="18" :color="employeeColor(selectedEmployee)">mdi-calendar-star</v-icon>
                        <span class="dialog-tile-label">Date d'embauche</span>
                        <span class="dialog-tile-value">{{ formatHireDate(selectedEmployee) }}</span>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <div class="dialog-tile">
                        <v-icon size="18" :color="employeeColor(selectedEmployee)">mdi-clock-outline</v-icon>
                        <span class="dialog-tile-label">Heures / semaine</span>
                        <span class="dialog-tile-value">{{ selectedEmployee.heures_hebdo || "—" }}h</span>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <div class="dialog-tile">
                        <v-icon size="18" :color="employeeColor(selectedEmployee)">mdi-calendar-week</v-icon>
                        <span class="dialog-tile-label">Jours travaillés</span>
                        <span class="dialog-tile-value">{{ selectedEmployee.nbr_jours_travaille_par_semaine || "—" }}j /
                          sem.</span>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <div class="dialog-tile">
                        <v-icon size="18" :color="employeeColor(selectedEmployee)">mdi-percent</v-icon>
                        <span class="dialog-tile-label">Taux d'activité</span>
                        <span class="dialog-tile-value">{{ selectedEmployee.taux_activité || "—" }} %</span>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <div class="dialog-tile"
                        :class="selectedEmployee.eligibilite_RTT == 1 ? 'dialog-tile--yes' : 'dialog-tile--no'">
                        <v-icon size="18" :color="selectedEmployee.eligibilite_RTT == 1 ? '#43a047' : '#e53935'">
                          {{ selectedEmployee.eligibilite_RTT == 1 ? "mdi-check-circle-outline" :
                            "mdi-close-circle-outline" }}
                        </v-icon>
                        <span class="dialog-tile-label">Éligibilité RTT</span>
                        <span class="dialog-tile-value"
                          :style="{ color: selectedEmployee.eligibilite_RTT == 1 ? '#43a047' : '#e53935' }">
                          {{ selectedEmployee.eligibilite_RTT == 1 ? "Oui" : "Non" }}
                        </span>
                      </div>
                    </v-col>
                  </v-row>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

          </v-card>

          <!-- Seniority -->
          <!-- <div v-if="selectedEmployee.jour_anciennete_total" class="dialog-seniority">
            <div class="dialog-seniority-header">
              <v-icon size="16" :color="employeeColor(selectedEmployee)">mdi-medal-outline</v-icon>
              <span>Jours d'ancienneté</span>
            </div>
            <div class="dialog-seniority-body">
              <div class="dialog-sen-stat">
                <span class="dialog-sen-num" :style="{ color: employeeColor(selectedEmployee) }">
                  {{ selectedEmployee.jour_anciennete_total || 0 }}
                </span>
                <span class="dialog-sen-label">Total acquis</span>
              </div>
              <div class="dialog-sen-div"></div>
              <div class="dialog-sen-stat">
                <span class="dialog-sen-num" style="color: #43a047">
                  {{ selectedEmployee.jour_anciennete_dispo || 0 }}
                </span>
                <span class="dialog-sen-label">Disponibles</span>
              </div>
            </div>
          </div> -->

          <!-- Mon solde -->
          <div class="dialog-section-label"
            style="margin-top: 16px; margin-bottom: 8px; display:flex; align-items:center;">
            Mon solde
            <span style="color:#9e9e9e; font-size:11px; font-weight:400; text-transform:none; margin-left:4px;">(année en cours)</span>
          </div>

          <div v-if="selectedEmployeeSoldeListe.length === 0" style="font-size:13px; color:#bdbdbd; margin-bottom:12px;">
            Aucun solde disponible
          </div>
          <div v-else class="adm-solde-list mb-3">

            <!-- Ligne CP -->
            <div v-if="selectedEmployeeSoldeCp.id_conge_type" class="adm-solde-line">
              <div class="adm-solde-line-name">
                <span class="adm-solde-dot" :style="{ background: getSoldeColor(selectedEmployeeSoldeCp) }"></span>
                <span class="adm-solde-line-label">{{ selectedEmployeeSoldeCp.conge_libelle || 'Congés Payés' }}</span>
              </div>
              <div class="adm-solde-line-groups">
                <div class="adm-solde-line-group">
                  <span class="adm-solde-group-badge adm-solde-group-badge--n1">N-1</span>
                  <span class="adm-solde-kv">
                    <span class="adm-solde-k">Acquis</span>
                    <span v-if="editingSoldeType !== selectedEmployeeSoldeCp.id_conge_type" class="adm-solde-v" style="color:#e53935;">{{ selectedEmployeeSoldeCp.cp_acquis_n_1 || 0 }}</span>
                    <input v-else v-model.number="editSoldeValues.cp_acquis_n_1" type="number" class="adm-solde-input" style="color:#e53935;" />
                  </span>
                  <span class="adm-solde-kv">
                    <span class="adm-solde-k">Pris</span>
                    <span v-if="editingSoldeType !== selectedEmployeeSoldeCp.id_conge_type" class="adm-solde-v" style="color:#9c27b0;">{{ selectedEmployeeSoldeCp.cp_reserves_n_1 || 0 }}</span>
                    <input v-else v-model.number="editSoldeValues.cp_reserves_n_1" type="number" class="adm-solde-input" style="color:#9c27b0;" />
                  </span>
                  <span class="adm-solde-kv adm-solde-kv--solde">
                    <span class="adm-solde-k">Solde</span>
                    <span v-if="editingSoldeType !== selectedEmployeeSoldeCp.id_conge_type" class="adm-solde-v" style="color:#3f51b5;">{{ selectedEmployeeSoldeCp.cp_solde_n_1 || 0 }}</span>
                    <input v-else v-model.number="editSoldeValues.cp_solde_n_1" type="number" class="adm-solde-input" style="color:#3f51b5;" />
                  </span>
                </div>
                <div class="adm-solde-line-sep"></div>
                <div class="adm-solde-line-group">
                  <span class="adm-solde-group-badge adm-solde-group-badge--n">N</span>
                  <span class="adm-solde-kv">
                    <span class="adm-solde-k">Acquis</span>
                    <span v-if="editingSoldeType !== selectedEmployeeSoldeCp.id_conge_type" class="adm-solde-v" style="color:#e53935;">{{ selectedEmployeeSoldeCp.cp_acquis_n || 0 }}</span>
                    <input v-else v-model.number="editSoldeValues.cp_acquis_n" type="number" class="adm-solde-input" style="color:#e53935;" />
                  </span>
                  <span class="adm-solde-kv">
                    <span class="adm-solde-k">Pris</span>
                    <span v-if="editingSoldeType !== selectedEmployeeSoldeCp.id_conge_type" class="adm-solde-v" style="color:#9c27b0;">{{ selectedEmployeeSoldeCp.cp_reserves_n || 0 }}</span>
                    <input v-else v-model.number="editSoldeValues.cp_reserves_n" type="number" class="adm-solde-input" style="color:#9c27b0;" />
                  </span>
                  <span class="adm-solde-kv adm-solde-kv--solde">
                    <span class="adm-solde-k">Solde</span>
                    <span v-if="editingSoldeType !== selectedEmployeeSoldeCp.id_conge_type" class="adm-solde-v" style="color:#3f51b5;">{{ selectedEmployeeSoldeCp.cp_solde_n || 0 }}</span>
                    <input v-else v-model.number="editSoldeValues.cp_solde_n" type="number" class="adm-solde-input" style="color:#3f51b5;" />
                  </span>
                </div>
              </div>
              <div v-if="allowSoldeEdit" class="adm-solde-actions">
                <template v-if="editingSoldeType !== selectedEmployeeSoldeCp.id_conge_type">
                  <v-btn icon size="x-small" variant="text" color="#9e9e9e" @click="openSoldeEdit(selectedEmployeeSoldeCp)">
                    <v-icon size="15">mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn icon size="x-small" variant="flat" color="#43a047" :loading="isSavingSolde" @click="saveSoldeEdit(selectedEmployeeSoldeCp)">
                    <v-icon size="15" color="white">mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon size="x-small" variant="flat" color="#e53935" @click="cancelSoldeEdit()">
                    <v-icon size="15" color="white">mdi-close</v-icon>
                  </v-btn>
                </template>
              </div>
            </div>

            <!-- Lignes autres types -->
            <div v-for="s in selectedEmployeeSoldeAutres" :key="s.id_conge_type" class="adm-solde-line">
              <div class="adm-solde-line-name">
                <span class="adm-solde-dot" :style="{ background: getSoldeColor(s) }"></span>
                <span class="adm-solde-line-label">{{ s.conge_libelle || s.id_conge_type }}</span>
              </div>
              <div class="adm-solde-line-groups">
                <div class="adm-solde-line-group">
                  <span class="adm-solde-kv">
                    <span class="adm-solde-k">Initial</span>
                    <span v-if="editingSoldeType !== s.id_conge_type" class="adm-solde-v" style="color:#e53935;">{{ s.solde_initial || 0 }}</span>
                    <input v-else v-model.number="editSoldeValues.solde_initial" type="number" class="adm-solde-input" style="color:#e53935;" />
                  </span>
                  <span class="adm-solde-kv">
                    <span class="adm-solde-k">Pris</span>
                    <span v-if="editingSoldeType !== s.id_conge_type" class="adm-solde-v" style="color:#9c27b0;">{{ s.solde_pris || 0 }}</span>
                    <input v-else v-model.number="editSoldeValues.solde_pris" type="number" class="adm-solde-input" style="color:#9c27b0;" />
                  </span>
                  <span class="adm-solde-kv adm-solde-kv--solde">
                    <span class="adm-solde-k">Disponible</span>
                    <span v-if="editingSoldeType !== s.id_conge_type" class="adm-solde-v" style="color:#3f51b5;">{{ s.solde_disponible || 0 }}</span>
                    <input v-else v-model.number="editSoldeValues.solde_disponible" type="number" class="adm-solde-input" style="color:#3f51b5;" />
                  </span>
                </div>
              </div>
              <div class="adm-solde-actions">
                <template v-if="editingSoldeType !== s.id_conge_type">
                  <v-btn icon size="x-small" variant="text" color="#9e9e9e" @click="openSoldeEdit(s)">
                    <v-icon size="15">mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <template v-else>
                  <v-btn icon size="x-small" variant="flat" color="#43a047" :loading="isSavingSolde" @click="saveSoldeEdit(s)">
                    <v-icon size="15" color="white">mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon size="x-small" variant="flat" color="#e53935" @click="cancelSoldeEdit()">
                    <v-icon size="15" color="white">mdi-close</v-icon>
                  </v-btn>
                </template>
              </div>
            </div>

          </div>

          <!-- Demandes en attente -->
          <div class="dialog-section-label" style="margin-top: 24px; margin-bottom: 10px;">
            <v-chip color="#fb8c00" style="font-weight: bold; font-size: 14px;" variant="tonal">
              <v-icon color="#fb8c00" size="18">mdi-clock-outline</v-icon>
              <span class="pl-1 pr-1">Demandes en attente :</span>
              {{ selectedEmployeePending.length }}
            </v-chip>
          </div>

          <v-data-table :headers="congeHeaders" :items="selectedEmployeePending" :items-per-page="-1" density="compact"
            style="border-radius: 10px;" class="dialog-requests-table pb-2" no-data-text="Aucune demande en attente">
            <template #item="{ item }">
              <tr class="pending-row" @click="openValidationModal(item.value)" style="cursor:pointer;"
                title="Cliquez pour traiter cette demande">
                <td>
                  <v-chip size="small" style="font-weight: bold;" :color="getTypeColor(item.value.id_conge_type)"
                    variant="tonal">
                    {{ getTypeName(item.value.id_conge_type) }}
                  </v-chip>
                </td>
                <td>{{ formatDate(item.value.debut_timestamp) }}</td>
                <td>{{ formatDate(item.value.fin_timestamp) }}</td>
                <td>{{ item.value.nbr_days || '—' }}</td>
                <td>
                  <motif-preview :commentaire="item.value.commentaire" :statut="item.value.statut" viewer="admin"
                    @open="openConversation(item.value)" />
                </td>
                <td>
                  <v-chip v-if="item.value.statut == 4" size="small" style="font-weight: bold;" color="#9c27b0"
                    variant="tonal">
                    <v-icon start size="12">mdi-trash-can-outline</v-icon>
                    Suppression demandée
                  </v-chip>
                  <v-chip v-else size="small" style="font-weight: bold;" color="#fb8c00" variant="tonal">
                    <v-icon start size="12">mdi-clock-outline</v-icon>
                    En attente
                  </v-chip>
                </td>
              </tr>
            </template>
            <template #bottom></template>
          </v-data-table>

          <!-- Historique -->
          <div class="dialog-section-label"
            style="margin-top: 24px; margin-bottom: 10px; justify-content: space-between;">
            <v-chip color="#3f51b5" style="font-weight: bold; font-size: 14px;" variant="tonal">
              <v-icon color="#3f51b5" size="18">mdi-history</v-icon>
              <span class="pl-1 pr-1">Historique :</span>
              {{ selectedEmployeeHistory.length }}
            </v-chip>
            <v-btn color="#43a047" variant="tonal" size="small" rounded="lg" @click="showExportDialog = true">
              <v-icon start size="16">mdi-microsoft-excel</v-icon>
              Exporter Excel
            </v-btn>
          </div>

          <v-data-table :headers="congeHeaders" :items="selectedEmployeeHistory" :items-per-page="5"
            items-per-page-text="Congés par page" density="compact" class="dialog-requests-table pb-2"
            no-data-text="Aucun historique disponible" style="border-radius: 10px;">
            <template #item="{ item }">
              <tr :class="item.value.statut == 1 ? 'history-row-admin-deletable' : ''">
                <td>
                  <v-chip size="small" style="font-weight: bold;" :color="getTypeColor(item.value.id_conge_type)"
                    variant="tonal">
                    {{ getTypeName(item.value.id_conge_type) }}
                  </v-chip>
                </td>
                <td>{{ formatDate(item.value.debut_timestamp) }}</td>
                <td>{{ formatDate(item.value.fin_timestamp) }}</td>
                <td>{{ item.value.nbr_days || '—' }}</td>
                <td>
                  <motif-preview :commentaire="item.value.commentaire" :statut="item.value.statut" viewer="admin"
                    @open="openConversation(item.value)" />
                </td>
                <td>
                  <div style="display:flex;align-items:center;gap:8px;">
                    <v-chip v-if="item.value.statut == 1" size="small" style="font-weight: bold;" color="#43a047"
                      variant="tonal">
                      <v-icon start size="12">mdi-check-circle-outline</v-icon>
                      Validée
                    </v-chip>
                    <v-chip v-else-if="item.value.statut == 2" size="small" style="font-weight: bold;" color="#e53935"
                      variant="tonal">
                      <v-icon start size="12">mdi-close-circle-outline</v-icon>
                      Refusée
                    </v-chip>
                    <v-chip v-else-if="item.value.statut == 3" size="small" style="font-weight: bold;" color="#9e9e9e"
                      variant="tonal">
                      <v-icon start size="12">mdi-cancel</v-icon>
                      Annulée
                    </v-chip>
                    <v-chip v-else-if="item.value.statut == 4" size="small" style="cursor:pointer;font-weight: bold;"
                      color="#9c27b0" variant="tonal" @click.stop="openValidationModal(item.value)">
                      <v-icon start size="12">mdi-trash-can-outline</v-icon>
                      Suppression demandée
                    </v-chip>
                    <v-chip v-else-if="item.value.statut == 5" size="small" style="font-weight: bold;" color="#4a148c"
                      variant="tonal">
                      <v-icon start size="12">mdi-trash-can</v-icon>
                      Supprimée
                    </v-chip>
                    <v-chip v-else size="small" style="font-weight: bold;" color="#757575" variant="tonal">
                      —
                    </v-chip>
                    <!-- Bouton suppression admin (uniquement sur les validées) -->
                    <v-btn v-if="item.value.statut == 1" icon size="x-small" variant="text" color="#e53935"
                      title="Supprimer ce congé" @click.stop="openAdminDeleteDialog(item.value)">
                      <v-icon size="16">mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ═══ Dialog suppression admin ═══ -->
    <v-dialog v-model="showAdminDeleteDialog" max-width="440" persistent>
      <v-card v-if="requestToAdminDelete" rounded="xl">
        <div class="form-dialog-header">
          <div class="form-dialog-icon" style="background:#f3e5f5;">
            <v-icon color="#9c27b0" size="22">mdi-trash-can-outline</v-icon>
          </div>
          <div>
            <div class="form-dialog-title">Supprimer ce congé</div>
            <div class="form-dialog-sub">{{ selectedEmployee ? selectedEmployee.user_name : '' }}</div>
          </div>
          <v-btn icon variant="text" color="#757575" size="small" class="ml-auto"
            @click="showAdminDeleteDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="form-dialog-body">
          <div class="cancel-recap">
            <div class="cancel-recap-row">
              <span class="cancel-recap-label">Type</span>
              <v-chip size="small" style="font-weight:bold;" :color="getTypeColor(requestToAdminDelete.id_conge_type)"
                variant="tonal">
                {{ getTypeName(requestToAdminDelete.id_conge_type) }}
              </v-chip>
            </div>
            <div class="cancel-recap-row">
              <span class="cancel-recap-label">Période</span>
              <span class="cancel-recap-value">
                {{ formatDate(requestToAdminDelete.debut_timestamp) }} → {{
                  formatDate(requestToAdminDelete.fin_timestamp)
                }}
              </span>
            </div>
            <div class="cancel-recap-row">
              <span class="cancel-recap-label">Jours</span>
              <span class="cancel-recap-value"><strong>{{ requestToAdminDelete.nbr_days || '—' }}</strong></span>
            </div>
            <div v-if="hasCommentaire(requestToAdminDelete.commentaire)" class="cancel-recap-row cancel-recap-row--stacked">
              <span class="cancel-recap-label">{{ motifLabel(requestToAdminDelete.commentaire) }}</span>
              <conversation-thread :commentaire="requestToAdminDelete.commentaire" viewer="admin"
                :employee-name="selectedEmployee ? selectedEmployee.user_name : ''" compact />
            </div>
          </div>
          <div class="cancel-warning" style="background:#f3e5f5;color:#9c27b0;">
            <v-icon size="16" color="#9c27b0">mdi-alert-outline</v-icon>
            Cette action est irréversible. Le congé sera définitivement supprimé.
          </div>
        </v-card-text>

        <v-card-actions class="form-dialog-actions">
          <v-btn variant="text" color="#757575" @click="showAdminDeleteDialog = false">Retour</v-btn>
          <v-spacer />
          <v-btn color="#9c27b0" variant="flat" rounded="lg" style="color:white;" :loading="isDeletingAdmin"
            @click="confirmAdminDelete">
            <v-icon start size="16">mdi-trash-can-outline</v-icon>
            Supprimer définitivement
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══ Dialog export global ═══ -->
    <v-dialog v-model="showGlobalExportDialog" max-width="400" persistent>
      <v-card rounded="xl">
        <div class="form-dialog-header">
          <div class="form-dialog-icon" style="background:#e8f5e9;">
            <v-icon color="#43a047" size="22">mdi-microsoft-excel</v-icon>
          </div>
          <div>
            <div class="form-dialog-title">Export global — Congés validés</div>
            <div class="form-dialog-sub">Tous les employés</div>
          </div>
          <v-btn icon variant="text" color="#757575" size="small" class="ml-auto"
            @click="showGlobalExportDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="form-dialog-body">
          <div class="form-label mb-3">Période</div>
          <div class="export-period-grid">
            <div class="export-period-chip" :class="{ 'export-period-chip--active': globalExportPeriod === 'current' }"
              @click="globalExportPeriod = 'current'">
              <v-icon size="18">mdi-calendar-today</v-icon>
              Mois en cours
            </div>
            <div class="export-period-chip" :class="{ 'export-period-chip--active': globalExportPeriod === 'previous' }"
              @click="globalExportPeriod = 'previous'">
              <v-icon size="18">mdi-calendar-arrow-left</v-icon>
              Mois précédent
            </div>
            <div class="export-period-chip" :class="{ 'export-period-chip--active': globalExportPeriod === 'all' }"
              @click="globalExportPeriod = 'all'">
              <v-icon size="18">mdi-calendar-blank-multiple</v-icon>
              Tout
            </div>
            <div class="export-period-chip" :class="{ 'export-period-chip--active': globalExportPeriod === 'custom' }"
              @click="globalExportPeriod = 'custom'">
              <v-icon size="18">mdi-calendar-range</v-icon>
              Personnalisé
            </div>
          </div>

          <v-row v-if="globalExportPeriod === 'custom'" class="mt-3">
            <v-col cols="6">
              <div class="form-label">Du</div>
              <input type="date" v-model="globalExportDateFrom" class="date-input" />
            </v-col>
            <v-col cols="6">
              <div class="form-label">Au</div>
              <input type="date" v-model="globalExportDateTo" class="date-input" />
            </v-col>
          </v-row>

          <div class="export-count-info mt-3">
            <v-icon size="15" color="#3f51b5">mdi-account-group-outline</v-icon>
            <span>{{ allUsers.length }} employé{{ allUsers.length !== 1 ? 's' : '' }} · export à la demande</span>
          </div>
        </v-card-text>

        <v-card-actions class="form-dialog-actions">
          <v-btn variant="text" color="#757575" @click="showGlobalExportDialog = false">Annuler</v-btn>
          <v-spacer />
          <v-btn color="#43a047" variant="flat" rounded="lg" style="color:white;" :loading="isGlobalExporting"
            @click="doGlobalExport">
            <v-icon start size="16">mdi-microsoft-excel</v-icon>
            Exporter
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══ Dialog export Excel ═══ -->
    <v-dialog v-model="showExportDialog" max-width="400" persistent>
      <v-card rounded="xl">
        <div class="form-dialog-header">
          <div class="form-dialog-icon" style="background:#e8f5e9;">
            <v-icon color="#43a047" size="22">mdi-microsoft-excel</v-icon>
          </div>
          <div>
            <div class="form-dialog-title">Exporter l'historique</div>
            <div class="form-dialog-sub">{{ selectedEmployee ? selectedEmployee.user_name : '' }}</div>
          </div>
          <v-btn icon variant="text" color="#757575" size="small" class="ml-auto" @click="showExportDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="form-dialog-body">
          <div class="form-label mb-3">Période</div>
          <div class="export-period-grid">
            <div class="export-period-chip" :class="{ 'export-period-chip--active': exportPeriod === 'current' }"
              @click="exportPeriod = 'current'">
              <v-icon size="18">mdi-calendar-today</v-icon>
              Mois en cours
            </div>
            <div class="export-period-chip" :class="{ 'export-period-chip--active': exportPeriod === 'previous' }"
              @click="exportPeriod = 'previous'">
              <v-icon size="18">mdi-calendar-arrow-left</v-icon>
              Mois précédent
            </div>
            <div class="export-period-chip" :class="{ 'export-period-chip--active': exportPeriod === 'all' }"
              @click="exportPeriod = 'all'">
              <v-icon size="18">mdi-calendar-blank-multiple</v-icon>
              Tout
            </div>
            <div class="export-period-chip" :class="{ 'export-period-chip--active': exportPeriod === 'custom' }"
              @click="exportPeriod = 'custom'">
              <v-icon size="18">mdi-calendar-range</v-icon>
              Personnalisé
            </div>
          </div>

          <v-row v-if="exportPeriod === 'custom'" class="mt-3">
            <v-col cols="6">
              <div class="form-label">Du</div>
              <input type="date" v-model="exportDateFrom" class="date-input" />
            </v-col>
            <v-col cols="6">
              <div class="form-label">Au</div>
              <input type="date" v-model="exportDateTo" class="date-input" />
            </v-col>
          </v-row>

          <div class="export-count-info mt-3">
            <v-icon size="15" color="#3f51b5">mdi-information-outline</v-icon>
            <span>{{ filteredHistoryForExport.length }} demande{{ filteredHistoryForExport.length !== 1 ? 's' : '' }} à
              exporter</span>
          </div>
        </v-card-text>

        <v-card-actions class="form-dialog-actions">
          <v-btn variant="text" color="#757575" @click="showExportDialog = false">Annuler</v-btn>
          <v-spacer />
          <v-btn color="#43a047" variant="flat" rounded="lg" style="color:white;"
            :disabled="filteredHistoryForExport.length === 0" @click="doExportExcel">
            <v-icon start size="16">mdi-microsoft-excel</v-icon>
            Exporter
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══ Modal nouvelle demande (admin) ═══ -->
    <v-dialog v-model="showNewRequestDialog" max-width="520" persistent>
      <v-card rounded="xl">
        <div class="form-dialog-header">
          <div class="form-dialog-icon">
            <v-icon color="#3f51b5" size="22">mdi-calendar-plus</v-icon>
          </div>
          <div>
            <div class="form-dialog-title">Nouvelle demande de congé</div>
            <div class="form-dialog-sub">{{ selectedEmployee ? selectedEmployee.user_name : '' }}</div>
          </div>
          <v-btn icon variant="text" color="#757575" size="small" class="ml-auto" @click="closeNewRequestForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="form-dialog-body">
          <v-form ref="adminRequestForm" v-model="adminFormValid" @submit.prevent="submitAdminRequest">

            <div class="form-label">Type de congé <span class="req">*</span></div>
            <v-select v-model="adminForm.type" :items="congeTypes" hide-details item-title="libelle"
              item-value="id_conge_type" variant="outlined" density="compact" rounded="lg"
              placeholder="Sélectionnez un type" :rules="[v => !!v || 'Champ requis']" class="mb-3">
              <template #item="{ item, props }">
                <v-list-item v-bind="props">
                  <template #prepend>
                    <div class="type-dot" :style="{ background: item.raw.color }"></div>
                  </template>
                </v-list-item>
              </template>
            </v-select>
            <div v-if="adminForm.type && adminForm.type.length > 0 && selectedEmployeeSoldeListe.find((val) => val.id_conge_type ==
              adminForm.type)">
              Solde de {{selectedEmployeeSoldeListe.find((val) => val.id_conge_type ==
                adminForm.type)['CONGE_TYPE::libelle']}} :
              <v-chip style="font-weight: bold;" density="compact" class="py-4"
                :color="congeTypes.find((val) => val.id_conge_type == adminForm.type).color">
                <span
                  v-if="selectedEmployeeSoldeListe.find((val) => val.id_conge_type == adminForm.type).solde_disponible && selectedEmployeeSoldeListe.find((val) => val.id_conge_type == adminForm.type).solde_disponible != ''">
                  {{selectedEmployeeSoldeListe.find((val) => val.id_conge_type == adminForm.type).solde_disponible}} J
                </span>
                <span v-else>
                  0 J
                </span>
              </v-chip>
            </div>
            <div v-if="adminForm.type && adminForm.type.length > 0" style="color: grey;font-size: 13px;">
              {{congeTypes.find((val) => val.id_conge_type == adminForm.type).description}}
            </div>

            <v-row class="mt-2">
              <v-col cols="6">
                <div class="form-label">Date de début <span class="req">*</span></div>
                <input type="date" :value="adminForm.dateDebut || ''"
                  @change="adminForm.dateDebut = $event.target.value || null; adminDateDebutError = ''; updateAdminNbrJours()"
                  class="date-input" :class="{ 'date-input--error': adminDateDebutError }" />
                <div v-if="adminDateDebutError" class="date-error-msg">{{ adminDateDebutError }}</div>
              </v-col>
              <v-col cols="6">
                <div class="form-label">Date de fin <span class="req">*</span></div>
                <input type="date" :value="adminForm.dateFin || ''"
                  @change="adminForm.dateFin = $event.target.value || null; adminDateFinError = ''; updateAdminNbrJours()"
                  class="date-input" :class="{ 'date-input--error': adminDateFinError }" />
                <div v-if="adminDateFinError" class="date-error-msg">{{ adminDateFinError }}</div>
              </v-col>
            </v-row>

            <div v-if="adminForm.dateDebut && adminForm.dateFin" class="days-summary mt-2">
              <v-icon size="16" color="#3f51b5">mdi-calendar-check-outline</v-icon>
              <input type="text" inputmode="decimal" v-model="adminForm.nbrJours"
                @blur="adminForm.nbrJours = roundHalf(adminForm.nbrJours)" class="jours-input" />
              <span>jour{{ Number(adminForm.nbrJours) >= 2 ? 's' : '' }} ouvré{{ Number(adminForm.nbrJours) >= 2 ? 's' : '' }}</span>
            </div>

            <div class="form-label" style="margin-top:8px;">Motif <span class="req">*</span></div>
            <v-textarea v-model="adminForm.motif" variant="outlined" density="compact" rounded="lg" rows="3"
              placeholder="Précisez le motif de la demande..." :rules="[v => !!v || 'Motif requis']" no-resize />

          </v-form>
        </v-card-text>

        <v-card-actions class="form-dialog-actions">
          <v-btn variant="text" color="#757575" @click="closeNewRequestForm">Annuler</v-btn>
          <v-spacer />
          <v-btn color="#3f51b5" variant="flat" rounded="lg" style="color:white;" :loading="isSubmittingAdmin"
            @click="submitAdminRequest">
            Envoyer la demande
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal validation demande -->
    <v-dialog v-model="showValidationModal" max-width="460" persistent>
      <v-card v-if="selectedRequest" rounded="xl">
        <div class="validation-header">
          <v-icon :color="selectedRequest.statut == 4 ? '#9c27b0' : '#3f51b5'" size="20">
            {{ selectedRequest.statut == 4 ? 'mdi-trash-can-outline' : 'mdi-calendar-check' }}
          </v-icon>
          <span class="validation-title">
            {{ selectedRequest.statut == 4 ? 'Demande de suppression' : 'Traitement de la demande' }}
          </span>
          <v-btn icon variant="text" color="#757575" size="small" class="ml-auto" @click="showValidationModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="validation-body">
          <div class="validation-row">
            <span class="validation-label">Employé</span>
            <span class="validation-value">{{ selectedEmployee?.user_name || '—' }}</span>
          </div>
          <div class="validation-row">
            <span class="validation-label">Type</span>
            <v-chip size="small" style="font-weight:bold;" :color="getTypeColor(selectedRequest.id_conge_type)"
              variant="tonal">
              {{ getTypeName(selectedRequest.id_conge_type) }}
            </v-chip>
          </div>
          <div class="validation-row">
            <span class="validation-label">Période</span>
            <span class="validation-value">{{ formatDate(selectedRequest.debut_timestamp) }} → {{
              formatDate(selectedRequest.fin_timestamp) }}</span>
          </div>
          <div class="validation-row">
            <span class="validation-label">Jours</span>
            <span class="validation-value"><strong>{{ selectedRequest.nbr_days || '—' }}</strong></span>
          </div>
          <div v-if="hasCommentaire(selectedRequest.commentaire)" class="validation-row validation-row--stacked">
            <span class="validation-label">{{ motifLabel(selectedRequest.commentaire) }}</span>
            <conversation-thread :commentaire="selectedRequest.commentaire" viewer="admin"
              :employee-name="selectedEmployee ? selectedEmployee.user_name : ''" compact />
          </div>

          <!-- Échange avec l'employé avant décision -->
          <div v-if="canEchanger(selectedRequest)" class="validation-reply">
            <v-btn v-if="!showValidationReply" variant="tonal" color="#3f51b5" size="small" rounded="lg"
              @click="openValidationReply">
              <v-icon start size="16" icon="mdi-reply" />
              Répondre à {{ selectedEmployeeFirstName }}
            </v-btn>
            <template v-else>
              <conversation-composer ref="validationComposer" v-model="validationReply" :sending="isSendingReply"
                :placeholder="`Votre message à ${selectedEmployeeFirstName}…`" @send="sendValidationReply" />
              <div class="validation-reply-hint" :class="{ 'validation-reply-hint--error': validationReplyError }">
                {{ validationReplyError || "La demande reste en attente : vous déciderez après sa réponse." }}
              </div>
            </template>
          </div>

          <!-- Motif admin obligatoire pour la suppression -->
          <template v-if="selectedRequest.statut == 4">
            <div style="width:100%;margin-top:12px;">
              <div class="form-label">Motif de décision <span class="req">*</span></div>
              <v-textarea v-model="adminDeleteMotif" variant="outlined" density="compact" rounded="lg" rows="2"
                placeholder="Précisez la raison de votre décision..." no-resize :rules="[v => !!v || 'Motif requis']" />
            </div>
          </template>
        </v-card-text>

        <v-card-actions class="validation-actions">
          <!-- Demande de suppression (statut 4) -->
          <template v-if="selectedRequest.statut == 4">
            <div style="display:flex;flex-direction:column;gap:8px;width:100%;">
              <v-btn variant="flat" color="#9c27b0" rounded="lg" style="color:white;" :loading="isUpdating"
                :disabled="!adminDeleteMotif || !adminDeleteMotif.trim()" block
                @click="withDecisionMotif('Suppression confirmée', cancelConge)">
                <v-icon start size="16">mdi-trash-can-outline</v-icon>
                Confirmer la suppression
              </v-btn>
              <v-btn variant="tonal" color="#757575" rounded="lg" :loading="isUpdating"
                :disabled="!adminDeleteMotif || !adminDeleteMotif.trim()" block
                @click="withDecisionMotif('Suppression refusée', cancelCancelRequest)">
                <v-icon start size="16">mdi-close-circle-outline</v-icon>
                Refuser la suppression
              </v-btn>
            </div>
          </template>
          <!-- Demande normale (statut 0) -->
          <template v-else>
            <v-btn variant="flat" color="#e53935" rounded="lg" style="color:white;" :loading="isUpdating"
              @click="deleteRequest(selectedRequest)">
              <v-icon class="pl-3" start size="16">mdi-close-circle-outline</v-icon>
              Refuser
            </v-btn>
            <v-spacer />
            <v-btn variant="flat" color="#43a047" rounded="lg" style="color:white;" :loading="isUpdating"
              @click="accepteRequest(selectedRequest)">
              <v-icon class="pl-3" start size="16">mdi-check-circle-outline</v-icon>
              Accepter
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══ Fenêtre motif & échanges ═══ -->
    <conversation-dialog v-model="showConversationDialog" :request="conversationRequest" viewer="admin"
      :employee-name="selectedEmployee ? selectedEmployee.user_name : ''" :author-name="userInfo.user_name || ''"
      :type-name="conversationRequest ? getTypeName(conversationRequest.id_conge_type) : ''"
      :type-color="conversationRequest ? getTypeColor(conversationRequest.id_conge_type) : undefined"
      decisions @updated="onConversationUpdated" @decision="onConversationDecision" />
  </div>
  <v-snackbar v-model="snackbar.value" :color="snackbar.color">
    <div align="center">
      {{ snackbar.msg }}
    </div>
  </v-snackbar>

</template>

<script>
import engine from "@/core/index";
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getConversation, getMotif, commentaireToText } from "@/core/commentaire";
import MotifPreview from "@/components/conversation/MotifPreview.vue";
import ConversationThread from "@/components/conversation/ConversationThread.vue";
import ConversationDialog from "@/components/conversation/ConversationDialog.vue";
import ConversationComposer from "@/components/conversation/ConversationComposer.vue";
import { ECHANGES_ACTIFS, echangeOuvert, envoyerMessage } from "@/core/conversation";

// ─── Mettre à true pour autoriser l'édition des soldes (CP, RTT, etc.) ───
const ALLOW_SOLDE_EDIT = true;

const COLOR_PALETTE = ['#3f51b5', '#e53935', '#43a047', '#fb8c00', '#9c27b0', '#00838f', '#f9a825'];
const COLOR_DARK = ['#283593', '#b71c1c', '#2e7d32', '#e65100', '#6a1b9a', '#005662', '#f57f17'];

export default {
  name: "HomeAdmin",
  components: { MotifPreview, ConversationThread, ConversationDialog, ConversationComposer },
  props: {
    get_basic_data: { type: Function },
    userInfo: { type: Object, default: () => ({}) },
    allUsers: { type: Array, default: () => [] },
    congeTypes: { type: Array, default: () => [] },
  },
  data() {
    return {
      snackbar: {},
      employeeSearch: "",
      selectedEmployee: null,
      showEmployeeDialog: false,
      selectedEmployeeCongeListe: [],
      selectedEmployeeSoldeListe: [],
      pendingCounts: {},
      hideZeroSoldeAdmin: true,
      allowSoldeEdit: ALLOW_SOLDE_EDIT,
      editingSoldeType: null,
      editSoldeValues: {},
      isSavingSolde: false,
      showValidationModal: false,
      selectedRequest: null,
      isUpdating: false,
      adminDeleteMotif: '',
      showConversationDialog: false,
      conversationRequest: null,
      showValidationReply: false,
      validationReply: '',
      isSendingReply: false,
      validationReplyError: '',
      showGlobalExportDialog: false,
      globalExportPeriod: 'all',
      globalExportDateFrom: null,
      globalExportDateTo: null,
      isGlobalExporting: false,
      showAdminDeleteDialog: false,
      requestToAdminDelete: null,
      isDeletingAdmin: false,
      showExportDialog: false,
      exportPeriod: 'all',
      exportDateFrom: null,
      exportDateTo: null,
      showNewRequestDialog: false,
      adminFormValid: false,
      isSubmittingAdmin: false,
      adminDateDebutError: '',
      adminDateFinError: '',
      adminForm: {
        type: null,
        dateDebut: null,
        dateFin: null,
        nbrJours: null,
        motif: '',
      },
      congeHeaders: [
        { title: 'Type', key: 'id_conge_type', sortable: true },
        { title: 'Date début', key: 'debut_timestamp', sortable: true },
        { title: 'Date fin', key: 'fin_timestamp', sortable: true },
        { title: 'Jours', key: 'nbr_days', sortable: true },
        { title: 'Motif', key: 'commentaire', sortable: false },
        { title: 'Statut', key: 'statut', sortable: true },
      ],
    };
  },
  watch: {
    allUsers: {
      handler(users) {
        if (users && users.length > 0) this.fetchAllPendingCounts(users);
      },
      immediate: true,
    },
  },
  computed: {
    selectedEmployeeSoldeCp() {
      return this.selectedEmployeeSoldeListe.find(s => s.id_conge_type === 'CP') || {};
    },
    selectedEmployeeSoldeAutres() {
      return this.selectedEmployeeSoldeListe.filter(s => s.id_conge_type !== 'CP');
    },
    filteredEmployees() {
      const q = (this.employeeSearch || "").toLowerCase().trim();
      if (!q) return this.allUsers;
      return this.allUsers.filter(emp => {
        const name = (emp.user_name || "").toLowerCase();
        const service = (emp.service || "").toLowerCase();
        return name.includes(q) || service.includes(q);
      });
    },
    selectedEmployeePending() {
      return this.selectedEmployeeCongeListe.filter(c => c.statut == 0 || c.statut == 4);
    },
    selectedEmployeeHistory() {
      return this.selectedEmployeeCongeListe.filter(c => c.statut > 0 && c.statut != 4);
    },
    filteredHistoryForExport() {
      return this.filterByPeriod(this.selectedEmployeeHistory);
    },
    selectedEmployeeFirstName() {
      return this.selectedEmployee ? this.employeeFirstName(this.selectedEmployee) : "l'employé";
    },
  },
  methods: {
    employeeFirstName(emp) {
      return (emp.user_name || "").split(" ")[0] || "—";
    },
    employeeLastName(emp) {
      const parts = (emp.user_name || "").split(" ");
      return parts.slice(1).join(" ").toUpperCase() || "";
    },
    employeeInitials(emp) {
      return (emp.user_name || "")
        .split(" ")
        .map(w => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "?";
    },
    employeeColorIndex(emp) {
      const key = emp.service || emp.user_name || "";
      let h = 0;
      for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) & 0xffff;
      return h % COLOR_PALETTE.length;
    },
    employeeColor(emp) { return COLOR_PALETTE[this.employeeColorIndex(emp)]; },
    employeeColorDark(emp) { return COLOR_DARK[this.employeeColorIndex(emp)]; },
    employeePendingCount(emp) {
      return this.pendingCounts[emp.user_id] || 0;
    },
    fetchAllPendingCounts(users) {
      let that = this;
      users.forEach(emp => {
        engine.get_conge_by_user_id(emp.user_id, function (data) {
          if (data.code == 0) {
            const count = data.data.data.data
              .map(v => v.fieldData)
              .filter(c => c.statut == 0).length;
            that.pendingCounts = { ...that.pendingCounts, [emp.user_id]: count };
          }
        });
      });
    },
    formatHireDate(emp) {
      const ts = parseInt(emp.date_embauche_timestamp);
      if (!ts) return "—";
      return new Date(ts * 1000).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
    },
    openEmployee(emp) {
      console.log("openEmployee", emp);

      this.selectedEmployee = emp;
      this.selectedEmployeeCongeListe = [];
      this.selectedEmployeeSoldeListe = [];
      this.showEmployeeDialog = true;
      let that = this;
      engine.get_conge_by_user_id(emp.user_id, function (data) {
        if (data.code == 0) {
          that.selectedEmployeeCongeListe = data.data.data.data.map(val => ({
            ...val.fieldData,
            _recordId: val.recordId,
          }));
        }
      });
      engine.get_solde_by_user_id(emp.user_id, function (data) {
        if (data.code == 0) {
          that.selectedEmployeeSoldeListe = data.data.data.data.map(val => ({ ...val.fieldData, _recordId: val.recordId }));
        }
      });
    },
    openValidationModal(request) {
      this.selectedRequest = request;
      this.adminDeleteMotif = '';
      this.showValidationReply = false;
      this.validationReply = '';
      this.validationReplyError = '';
      this.showValidationModal = true;
      this.isUpdating = false;
    },
    cancelCancelRequest(selectedRequest) {
      let that = this
      this.isUpdating = true;
      engine.run_conge_function('demande.accepter', { "id_demande": selectedRequest.id_demande }, function (data) {
        that.isUpdating = false;
        that.showValidationModal = false;
        if (data.code == 0) {
          that.get_basic_data()
          that.openEmployee(that.selectedEmployee)
          that.snackbar = {
            color: 'green',
            value: true,
            msg: 'Modification enregistrée'
          }
        } else {
          that.snackbar = {
            color: 'red',
            value: true,
            msg: 'Une erreur est survenue'
          }
        }
      })
    },
    cancelConge(selectedRequest) {
      let that = this
      this.isUpdating = true;
      engine.run_conge_function('demande.accepter', { "id_demande": selectedRequest.id_demande }, function (data) {
        that.isUpdating = false;
        that.showValidationModal = false;
        if (data.code == 0) {
          that.get_basic_data()
          that.openEmployee(that.selectedEmployee)
          that.snackbar = {
            color: 'green',
            value: true,
            msg: 'Modification enregistrée'
          }
        } else {
          that.snackbar = {
            color: 'red',
            value: true,
            msg: 'Une erreur est survenue'
          }
        }
      })
    },
    deleteRequest(selectedRequest) {
      let that = this
      this.isUpdating = true;
      engine.run_conge_function('demande.refuser', { "id_demande": selectedRequest.id_demande }, function (data) {
        that.isUpdating = false;
        that.showValidationModal = false;
        if (data.code == 0) {
          that.get_basic_data()
          that.openEmployee(that.selectedEmployee)
          that.snackbar = {
            color: 'green',
            value: true,
            msg: 'Modification enregistrée'
          }
        } else {
          that.snackbar = {
            color: 'red',
            value: true,
            msg: 'Une erreur est survenue'
          }
        }
      })
    },
    accepteRequest(selectedRequest) {
      let that = this
      this.isUpdating = true;
      engine.run_conge_function('demande.accepter', { "id_demande": selectedRequest.id_demande }, function (data) {
        that.isUpdating = false;
        that.showValidationModal = false;
        if (data.code == 0) {
          that.get_basic_data()
          that.openEmployee(that.selectedEmployee)
          that.snackbar = {
            color: 'green',
            value: true,
            msg: 'Modification enregistrée'
          }
        } else {
          that.snackbar = {
            color: 'red',
            value: true,
            msg: 'Une erreur est survenue'
          }
        }
      })
    },
    // handleDecision(statut) {
    //   if (!this.selectedRequest?._recordId) return;
    //   this.isUpdating = true;
    //   let that = this;
    //   engine.update_conge_statut(this.selectedRequest._recordId, statut, function () {
    //     that.isUpdating = false;
    //     that.showValidationModal = false;
    //     that.selectedRequest = null;
    //     // Recharger les congés de l'employé
    //     engine.get_conge_by_user_id(that.selectedEmployee.user_id, function (data) {
    //       if (data.code == 0) {
    //         that.selectedEmployeeCongeListe = data.data.data.data.map(val => ({
    //           ...val.fieldData,
    //           _recordId: val.recordId,
    //         }));
    //       }
    //     });
    //     // Mettre à jour le badge de la carte employé
    //     engine.get_conge_by_user_id(that.selectedEmployee.user_id, function (data) {
    //       if (data.code == 0) {
    //         const count = data.data.data.data
    //           .map(v => v.fieldData)
    //           .filter(c => c.statut == 0).length;
    //         that.pendingCounts = { ...that.pendingCounts, [that.selectedEmployee.user_id]: count };
    //       }
    //     });
    //   });
    // },
    openSoldeEdit(s) {
      this.editingSoldeType = s.id_conge_type;
      // CP
      this.editSoldeValues = {
        cp_acquis_n: s.cp_acquis_n || 0,
        cp_acquis_n_1: s.cp_acquis_n_1 || 0,
        cp_reserves_n: s.cp_reserves_n || 0,
        cp_reserves_n_1: s.cp_reserves_n_1 || 0,
        cp_solde_n: s.cp_solde_n || 0,
        cp_solde_n_1: s.cp_solde_n_1 || 0,
        // autres types
        solde_initial: s.solde_initial || 0,
        solde_pris: s.solde_pris || 0,
        solde_disponible: s.solde_disponible || 0,
      };
    },
    cancelSoldeEdit() {
      this.editingSoldeType = null;
      this.editSoldeValues = {};
    },
    saveSoldeEdit(s) {
      if (!s._recordId) return;
      this.isSavingSolde = true;
      let that = this;
      // Champs à sauvegarder selon le type
      let fields = s.id_conge_type === 'CP'
        ? {
            cp_acquis_n: this.editSoldeValues.cp_acquis_n,
            cp_acquis_n_1: this.editSoldeValues.cp_acquis_n_1,
            cp_reserves_n: this.editSoldeValues.cp_reserves_n,
            cp_reserves_n_1: this.editSoldeValues.cp_reserves_n_1,
            cp_solde_n: this.editSoldeValues.cp_solde_n,
            cp_solde_n_1: this.editSoldeValues.cp_solde_n_1,
          }
        : {
            solde_initial: this.editSoldeValues.solde_initial,
            solde_pris: this.editSoldeValues.solde_pris,
            solde_disponible: this.editSoldeValues.solde_disponible,
          };
      engine.update_solde(s._recordId, fields, function () {
        that.isSavingSolde = false;
        that.editingSoldeType = null;
        that.editSoldeValues = {};
        // Recharger les soldes de l'employé
        engine.get_solde_by_user_id(that.selectedEmployee.user_id, function (data) {
          if (data.code == 0) {
            that.selectedEmployeeSoldeListe = data.data.data.data.map(val => ({ ...val.fieldData, _recordId: val.recordId }));
          }
        });
      });
    },
    getSoldeColor(solde) {
      const colorMap = {
        red: '#e53935', blue: '#3f51b5', green: '#43a047',
        orange: '#fb8c00', yellow: '#f9a825', purple: '#9c27b0',
        gray: '#757575', grey: '#757575', pink: '#e91e63',
        teal: '#00838f', cyan: '#00acc1', indigo: '#3f51b5',
      };
      const found = this.congeTypes.find(t => t.id_conge_type === solde.id_conge_type);
      if (!found) return '#3f51b5';
      return colorMap[found.color] || found.color || '#3f51b5';
    },
    getTypeName(id) {
      const found = this.congeTypes.find(t => t.id_conge_type === id);
      return found ? found.libelle : id || "—";
    },
    getTypeColor(id) {
      const colorMap = {
        red: '#e53935', blue: '#3f51b5', green: '#43a047',
        orange: '#fb8c00', yellow: '#f9a825', purple: '#9c27b0',
        gray: '#757575', grey: '#757575', pink: '#e91e63',
        teal: '#00838f', cyan: '#00acc1', indigo: '#3f51b5',
      };
      const found = this.congeTypes.find(t => t.id_conge_type === id);
      if (!found) return '#757575';
      return colorMap[found.color] || found.color || '#757575';
    },
    formatDate(ts) {
      if (!ts) return "—";
      return new Date(parseInt(ts) * 1).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
      });
    },
    // Motif seul, débarrassé du JSON de la conversation (exports, récapitulatifs)
    parseMotif(commentaire) {
      return getMotif(commentaire) || "—";
    },
    hasCommentaire(commentaire) {
      return getConversation(commentaire).messages.length > 0;
    },
    // Commentaire libre affiché sur une ligne (gère aussi le format JSON)
    formatCommentaire(commentaire) {
      return commentaireToText(commentaire, " · ");
    },
    openConversation(item) {
      this.conversationRequest = item;
      this.showConversationDialog = true;
    },
    // Message envoyé depuis « Motif & échanges » : mise à jour de la ligne concernée
    onConversationUpdated({ request, commentaire }) {
      request.commentaire = commentaire;
    },
    // Décision prise depuis « Motif & échanges » : mêmes actions que la fenêtre de traitement
    onConversationDecision({ action, request }) {
      const actions = {
        accepter: this.accepteRequest,
        refuser: this.deleteRequest,
        'confirmer-suppression': this.cancelConge,
        'refuser-suppression': this.cancelCancelRequest,
      };
      if (!actions[action]) return;
      this.showConversationDialog = false;
      actions[action](request);
    },
    motifLabel(commentaire) {
      return getConversation(commentaire).replyCount ? 'Motif & échanges' : 'Motif';
    },
    canEchanger(request) {
      return echangeOuvert(request);
    },
    openValidationReply() {
      this.showValidationReply = true;
      this.$nextTick(() => {
        if (this.$refs.validationComposer) this.$refs.validationComposer.focus();
      });
    },
    sendValidationReply(texte) {
      const request = this.selectedRequest;
      if (!request || this.isSendingReply) return;
      this.isSendingReply = true;
      this.validationReplyError = '';
      envoyerMessage({ request, role: 'admin', nom: this.userInfo.user_name, texte }, (res) => {
        this.isSendingReply = false;
        if (!res.ok) {
          this.validationReplyError = res.message;
          return;
        }
        request.commentaire = res.commentaire;
        this.validationReply = '';
        this.showValidationReply = false;
        this.snackbar = { color: 'green', value: true, msg: 'Message envoyé' };
      });
    },
    // Le motif de décision rejoint la conversation, puis la décision est appliquée
    withDecisionMotif(prefix, action) {
      const request = this.selectedRequest;
      const motif = (this.adminDeleteMotif || '').trim();
      if (!request || !motif || !ECHANGES_ACTIFS) return action(request);
      this.isUpdating = true;
      envoyerMessage({ request, role: 'admin', nom: this.userInfo.user_name, texte: `${prefix} : ${motif}` }, (res) => {
        if (res.ok) request.commentaire = res.commentaire;
        else console.warn("Motif de décision non ajouté à la conversation :", res.message);
        action(request);
      });
    },
    adminWorkingDays() {
      const { dateDebut, dateFin } = this.adminForm;
      if (!dateDebut || !dateFin || dateFin < dateDebut) return 0;
      let count = 0;
      const cur = new Date(dateDebut);
      const end = new Date(dateFin);
      while (cur <= end) {
        const d = cur.getDay();
        if (d !== 0 && d !== 6) count++;
        cur.setDate(cur.getDate() + 1);
      }
      return count;
    },
    updateAdminNbrJours() {
      this.$nextTick(() => {
        this.adminForm.nbrJours = this.adminWorkingDays() || null;
      });
    },
    // Arrondit au demi-jour le plus proche (0.5), accepte la virgule ou le point
    roundHalf(v) {
      const n = parseFloat(String(v).replace(',', '.'));
      if (isNaN(n) || n <= 0) return null;
      return Math.round(n * 2) / 2;
    },
    closeNewRequestForm() {
      this.showNewRequestDialog = false;
      this.$nextTick(() => {
        this.adminForm = { type: null, dateDebut: null, dateFin: null, nbrJours: null, motif: '' };
        this.adminDateDebutError = '';
        this.adminDateFinError = '';
        this.$refs.adminRequestForm?.reset();
      });
    },
    async submitAdminRequest() {
      this.adminDateDebutError = '';
      this.adminDateFinError = '';
      if (!this.adminForm.dateDebut) this.adminDateDebutError = 'Champ requis';
      if (!this.adminForm.dateFin) this.adminDateFinError = 'Champ requis';
      else if (this.adminForm.dateDebut && this.adminForm.dateFin < this.adminForm.dateDebut)
        this.adminDateFinError = 'Doit être après le début';
      if (this.adminDateDebutError || this.adminDateFinError) return;

      const { valid } = await this.$refs.adminRequestForm.validate();
      if (!valid) return;

      this.isSubmittingAdmin = true;
      let that = this;
      const payload = {
        statut: 0,
        id_conge_type: this.adminForm.type,
        user_id: this.selectedEmployee.user_id,
        debut_timestamp: parseInt(moment(this.adminForm.dateDebut).format('x')),
        fin_timestamp: parseInt(moment(this.adminForm.dateFin).format('x')),
        commentaire: JSON.stringify([{ user: this.adminForm.motif }]),
        nbr_days: this.roundHalf(this.adminForm.nbrJours) ?? this.adminWorkingDays(),
      };

      engine.create_demande_conge(payload, function () {
        that.isSubmittingAdmin = false;
        that.closeNewRequestForm();
        // Recharger les congés de l'employé
        engine.get_conge_by_user_id(that.selectedEmployee.user_id, function (data) {
          if (data.code == 0) {
            that.selectedEmployeeCongeListe = data.data.data.data.map(val => ({
              ...val.fieldData,
              _recordId: val.recordId,
            }));
          }
        });
      });
    },
    async doGlobalExport() {
      this.isGlobalExporting = true;

      // Récupérer les congés de tous les employés en parallèle
      const results = await Promise.all(
        this.allUsers.map(user =>
          new Promise(resolve => {
            engine.get_conge_by_user_id(user.user_id, data => {
              if (data.code == 0) {
                resolve({ user, conges: data.data.data.data.map(v => v.fieldData) });
              } else {
                resolve({ user, conges: [] });
              }
            });
          })
        )
      );

      // Filtrer : validées (statut == 1) + période
      const now = new Date();
      let startDate = null;
      let endDate = null;
      if (this.globalExportPeriod === 'current') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      } else if (this.globalExportPeriod === 'previous') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      } else if (this.globalExportPeriod === 'custom') {
        startDate = this.globalExportDateFrom ? new Date(this.globalExportDateFrom) : null;
        endDate = this.globalExportDateTo ? new Date(this.globalExportDateTo + 'T23:59:59') : null;
      }

      const rows = [];
      results.forEach(({ user, conges }) => {
        conges
          .filter(c => {
            if (c.statut != 1) return false;
            const d = new Date(parseInt(c.debut_timestamp));
            if (startDate && d < startDate) return false;
            if (endDate && d > endDate) return false;
            return true;
          })
          .forEach(c => {
            rows.push({
              'Employé': user.user_name || '—',
              'Service': user.service || '—',
              'Type': this.getTypeName(c.id_conge_type),
              'Date début': this.formatDate(c.debut_timestamp),
              'Date fin': this.formatDate(c.fin_timestamp),
              'Jours': c.nbr_days || '—',
              'Motif': this.parseMotif(c.commentaire),
            });
          });
      });

      // Trier par employé puis par date de début
      rows.sort((a, b) => a['Employé'].localeCompare(b['Employé']) || a['Date début'].localeCompare(b['Date début']));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 24 }, { wch: 18 }, { wch: 22 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 36 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Congés validés');

      const periodLabel = this.globalExportPeriod === 'current' ? moment().format('YYYY-MM')
        : this.globalExportPeriod === 'previous' ? moment().subtract(1, 'month').format('YYYY-MM')
          : this.globalExportPeriod === 'custom' ? `${this.globalExportDateFrom || ''}_${this.globalExportDateTo || ''}`
            : 'complet';

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }),
        `conges_valides_tous_employes_${periodLabel}.xlsx`);

      this.isGlobalExporting = false;
      this.showGlobalExportDialog = false;
    },
    openAdminDeleteDialog(item) {
      this.requestToAdminDelete = item;
      this.showAdminDeleteDialog = true;
    },
    confirmAdminDelete() {
      if (!this.requestToAdminDelete?._recordId) return;
      this.isDeletingAdmin = true;
      let that = this;
      engine.update_conge_statut(this.requestToAdminDelete._recordId, 5, function () {
        that.isDeletingAdmin = false;
        that.showAdminDeleteDialog = false;
        that.requestToAdminDelete = null;
        engine.get_conge_by_user_id(that.selectedEmployee.user_id, function (data) {
          if (data.code == 0) {
            that.selectedEmployeeCongeListe = data.data.data.data.map(val => ({
              ...val.fieldData,
              _recordId: val.recordId,
            }));
          }
        });
      });
    },
    filterByPeriod(items) {
      const now = new Date();
      let startDate = null;
      let endDate = null;
      if (this.exportPeriod === 'current') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      } else if (this.exportPeriod === 'previous') {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      } else if (this.exportPeriod === 'custom') {
        startDate = this.exportDateFrom ? new Date(this.exportDateFrom) : null;
        endDate = this.exportDateTo ? new Date(this.exportDateTo + 'T23:59:59') : null;
      } else {
        return items;
      }
      return items.filter(item => {
        const d = new Date(parseInt(item.debut_timestamp));
        if (startDate && d < startDate) return false;
        if (endDate && d > endDate) return false;
        return true;
      });
    },
    doExportExcel() {
      const statusLabel = (statut) => {
        if (statut == 1) return 'Validée';
        if (statut == 2) return 'Refusée';
        return 'En attente';
      };

      const employeeName = this.selectedEmployee?.user_name || 'export';

      const rows = this.filteredHistoryForExport.map((item) => ({
        'Type': this.getTypeName(item.id_conge_type),
        'Date début': this.formatDate(item.debut_timestamp),
        'Date fin': this.formatDate(item.fin_timestamp),
        'Jours': item.nbr_days || '—',
        'Motif': this.parseMotif(item.commentaire),
        'Statut': statusLabel(item.statut),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 22 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 36 }, { wch: 12 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Historique');

      const periodLabel = this.exportPeriod === 'current' ? moment().format('YYYY-MM')
        : this.exportPeriod === 'previous' ? moment().subtract(1, 'month').format('YYYY-MM')
          : this.exportPeriod === 'custom' ? `${this.exportDateFrom || ''}_${this.exportDateTo || ''}`
            : 'complet';
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileName = `historique_conges_${employeeName}_${periodLabel}.xlsx`;
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
      this.showExportDialog = false;
    },
  },
};
</script>

<style scoped>
.admin-dashboard {
  padding: 32px 40px;
  background: #f0f2f5;
  min-height: calc(100vh - 64px);
}

.welcome-section {
  margin-bottom: 28px;
}

.welcome-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.welcome-subtitle {
  font-size: 14px;
  color: #757575;
  margin: 4px 0 0 0;
}

/* ── Employees section ── */
.employees-section {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(63, 81, 181, 0.07);
  padding: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.emp-search {
  margin-bottom: 20px;
}

.emp-grid {
  margin-top: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
  color: #bdbdbd;
  font-size: 15px;
}

/* ── Employee card ── */
.emp-card {
  position: relative;
  background: #fafbff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 16px 18px;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
  height: 100%;
}

.emp-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(63, 81, 181, 0.13);
  background: white;
}

.emp-badge-dot {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e53935;
  color: white;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(229, 57, 53, 0.4);
}

.emp-names {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.emp-firstname {
  font-size: 14px;
  font-weight: 500;
  color: #424242;
  line-height: 1.3;
}

.emp-lastname {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.3;
}

.emp-service {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #9e9e9e;
  margin-top: 8px;
}

/* ── Dialog ── */
.dialog-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 24px 20px;
  background: white;
}

.dialog-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
}

.dialog-info {
  flex: 1;
  min-width: 0;
}

.dialog-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.dialog-email {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #757575;
}

.dialog-comment {
  font-size: 12px;
  color: #9e9e9e;
  font-style: italic;
  margin-top: 4px;
}

.dialog-body {
  padding: 20px 24px 24px !important;
  background: #f9fafc;
}

.dialog-section-label {
  font-size: 13px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.dialog-tile {
  border: solid 1px rgb(0, 0, 0, 0.1);
  background: white;
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
}

.dialog-tile--yes {
  border-left: 3px solid #43a047;
}

.dialog-tile--no {
  border-left: 3px solid #e53935;
}

.dialog-tile-label {
  font-size: 11px;
  font-weight: 600;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.dialog-tile-value {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
}

.dialog-seniority {
  background: white;
  border-radius: 12px;
  padding: 14px 18px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
  margin-bottom: 4px;
}

.dialog-seniority-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 12px;
}

.dialog-seniority-body {
  display: flex;
  align-items: center;
  gap: 20px;
}

.dialog-sen-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 50px;
}

.dialog-sen-num {
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
}

.dialog-sen-label {
  font-size: 11px;
  color: #9e9e9e;
  font-weight: 500;
  margin-top: 2px;
}

.dialog-sen-div {
  width: 1px;
  height: 34px;
  background: #f0f0f0;
}

/* ── Requests table ── */
.solde-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(63, 81, 181, 0.07);
  overflow: hidden;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-left: 4px solid transparent;
}

.solde-body {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.solde-title {
  font-size: 12px;
  font-weight: 600;
  color: #424242;
  margin: 0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.solde-main {
  display: flex;
  align-items: baseline;
  gap: 3px;
}

.solde-dispo {
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
}

.solde-unit {
  font-size: 11px;
  color: #757575;
  font-weight: 500;
}

.solde-footer {
  font-size: 11px;
  color: #9e9e9e;
  white-space: nowrap;
  display: flex;
  gap: 2px;
}

.solde-initial {
  font-weight: 500;
}

.requests-table-wrapper {
  overflow-x: auto;
}

.requests-table {
  width: 100%;
  border-collapse: collapse;
}

.requests-table th {
  text-align: left;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #f5f5f5;
}

.requests-table td {
  padding: 12px 14px;
  font-size: 14px;
  color: #424242;
  border-bottom: 1px solid #fafafa;
}

.requests-table tbody tr:hover {
  background: #fafbff;
}

.period-cell {
  font-size: 13px;
  color: #616161;
  white-space: nowrap;
}

.days-cell {
  font-size: 15px;
  color: #1a1a2e;
}

@media (max-width: 600px) {
  .admin-dashboard {
    padding: 20px 16px;
  }

  .dialog-header {
    padding: 18px 16px 16px;
  }

  .dialog-body {
    padding: 16px !important;
  }
}

.pending-row:hover td {
  background: #fff8e1;
}

.history-row-admin-deletable:hover td {
  background: #fce4ec;
}

/* ── Formulaire nouvelle demande (admin) ── */
.form-dialog-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.form-dialog-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #e8eaf6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.form-dialog-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
}

.form-dialog-sub {
  font-size: 12px;
  color: #9e9e9e;
  margin-top: 2px;
}

.form-dialog-body {
  padding: 20px 20px 8px !important;
}

.form-dialog-actions {
  padding: 12px 20px 18px !important;
}

.form-label {
  font-size: 12px;
  font-weight: 600;
  color: #424242;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.req {
  color: #e53935;
}

.type-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
}

.date-input {
  width: 100%;
  height: 40px;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #424242;
  background: white;
  cursor: pointer;
  box-sizing: border-box;
  font-family: inherit;
}

.date-input:focus {
  outline: none;
  border: 2px solid #3f51b5;
}

.date-input--error {
  border-color: #e53935 !important;
}

.date-error-msg {
  font-size: 12px;
  color: #e53935;
  margin-top: 4px;
  padding-left: 4px;
}

.days-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #e8eaf6;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  color: #3f51b5;
  margin-bottom: 12px;
}

.jours-input {
  width: 48px;
  text-align: center;
  border: none;
  border-bottom: 2px solid #3f51b5;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  color: #3f51b5;
  padding: 0 2px;
  outline: none;
  font-family: inherit;
}

/* ── Cancel / Delete recap ── */
.cancel-recap {
  background: #fafafa;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
  border: 1px solid #f0f0f0;
}

.cancel-recap-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.cancel-recap-label {
  font-size: 12px;
  font-weight: 600;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 64px;
}

.cancel-recap-value {
  font-size: 14px;
  color: #424242;
}

.cancel-recap-row--stacked {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

.cancel-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  background: #ffebee;
  color: #e53935;
  border-radius: 8px;
  padding: 10px 14px;
  font-weight: 500;
}

/* ── Export period ── */
.export-period-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.export-period-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 2px solid #e8eaf6;
  font-size: 13px;
  font-weight: 600;
  color: #424242;
  cursor: pointer;
  transition: all 0.15s;
  background: #fafafa;
}

.export-period-chip:hover {
  border-color: #3f51b5;
  background: #f3f4fb;
  color: #3f51b5;
}

.export-period-chip--active {
  border-color: #3f51b5;
  background: #e8eaf6;
  color: #3f51b5;
}

.export-count-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #757575;
  background: #f3f4fb;
  border-radius: 8px;
  padding: 8px 12px;
}

.validation-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #f0f0f0;
}

.validation-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
}

.validation-body {
  padding: 16px 20px !important;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.validation-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.validation-label {
  font-size: 12px;
  font-weight: 600;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 70px;
}

.validation-value {
  font-size: 14px;
  color: #424242;
}

.validation-row--stacked {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

/* ── Réponse avant décision ── */
.validation-reply {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.validation-reply-hint {
  padding: 0 10px;
  font-size: 11.5px;
  color: #9e9e9e;
}

.validation-reply-hint--error {
  color: #e53935;
}

.validation-actions {
  padding: 12px 20px 18px !important;
}

/* ── Admin solde lignes compactes ── */
.adm-solde-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.adm-solde-line {
  display: flex;
  align-items: center;
  gap: 14px;
  background: white;
  border-radius: 10px;
  padding: 10px 16px;
  box-shadow: 0 1px 6px rgba(63, 81, 181, 0.06);
  flex-wrap: wrap;
}

.adm-solde-line-name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 130px;
  flex-shrink: 0;
}

.adm-solde-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.adm-solde-line-label {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a2e;
  white-space: nowrap;
}

.adm-solde-line-groups {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
}

.adm-solde-line-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.adm-solde-group-badge {
  font-size: 10px;
  font-weight: 800;
  border-radius: 5px;
  padding: 2px 7px;
  color: white;
  letter-spacing: 0.3px;
}

.adm-solde-group-badge--n {
  background: #3f51b5;
}

.adm-solde-group-badge--n1 {
  background: #607d8b;
}

.adm-solde-line-sep {
  width: 1px;
  height: 24px;
  background: #e0e0e0;
  flex-shrink: 0;
}

.adm-solde-kv {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.adm-solde-k {
  font-size: 11px;
  color: #9e9e9e;
  font-weight: 500;
}

.adm-solde-v {
  font-size: 14px;
  font-weight: 700;
}

.adm-solde-kv--solde .adm-solde-k {
  font-weight: 600;
  color: #616161;
}

.adm-solde-kv--solde .adm-solde-v {
  font-size: 16px;
  font-weight: 800;
}

.adm-solde-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  flex-shrink: 0;
}

.adm-solde-input {
  width: 52px;
  border: none;
  border-bottom: 2px solid #3f51b5;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  outline: none;
  padding: 0 2px;
  font-family: inherit;
  -moz-appearance: textfield;
}

.adm-solde-input::-webkit-inner-spin-button,
.adm-solde-input::-webkit-outer-spin-button {
  opacity: 0;
}
</style>
